import { getDatabase, ref, update, get, set, remove, child, serverTimestamp } from "firebase/database";
import {fetchCourseContent, getTotalQsByLocation} from "../components/courseContent/courseObjects";

export function writePersonalName(uid, name) {
  const db = getDatabase();
  uid && update(ref(db, '/users/' + uid), {
    name: name
  });
}

export function writePersonalNameAndAvatar(uid, name, color, character) {
  const db = getDatabase();
  uid && update(ref(db, '/users/' + uid), {
    name: name,
    color: color,
    character: character
  });
}

export function writeUserIntent(uid, userIntent) {
  const db = getDatabase();
  uid && update(ref(db, '/users/' + uid), {
    userIntent: userIntent
  });
}

export function writeUserCourseCompletion(uid, courseName, completionPayload) {
  if (!uid || !courseName || !completionPayload) return;
  const db = getDatabase();
  const path = `/users/${uid}/courseCompletions/${courseName}`;
  update(ref(db, path), completionPayload);
}

export function writeTeamName(teamId, teamName) {
  const db = getDatabase();
  teamId && update(ref(db, '/teams/' + teamId), {
    teamName: teamName
  });
}

export function writeTeamProgress(teamId, course, progress) {
  const db = getDatabase();
  const path = '/teams/' + teamId + '/courseProgress/' + course;
  teamId && set(ref(db, path),progress);
}

export function writeTeamQuestionHistory(teamId, question, payload) {
  const db = getDatabase();
  const path = '/teams/' + teamId + '/questions/' + question;
  teamId && update(ref(db, path), payload);
}

export function writeCourseSelection(teamId, course) {
  const totalQsByLocation = getTotalQsByLocation(fetchCourseContent(course));
  //for Erik: look into how we can remove this courseSummary nonsense. I don't think we need it on the back-end
  const db = getDatabase();
  teamId && update(ref(db, '/teams/' + teamId), {
    currentCourseSelection: course,
    courseSummary: totalQsByLocation
  });
}

export const getMemberIdsForGivenTeamId = (currentTeamId) => {
  return new Promise((resolve, reject) => {
    const dbRef = ref(getDatabase());
    get(child(dbRef, 'teams/' + currentTeamId + '/memberIds'))
      .then((snapshot) => {
        const memberIds = snapshot.val();
        resolve(memberIds);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

/**
 * @param {string} uid - current user id
 * @param {string} currentTeamId - team to join or create
 * @param {string[]} [newMemberIdsArray] - optional; when omitted we read or create member list
 * @param {string} [hostId] - when creating a new team (host/solo), pass uid to set host and write team in one update
 */
export async function writeCurrentTeamIdData(uid, currentTeamId, newMemberIdsArray, hostId) {
  const db = getDatabase();
  uid && update(ref(db, '/users/' + uid), {
    currentTeamId: currentTeamId,
  });

  let newMemberIds = newMemberIdsArray || [];
  if (!newMemberIdsArray) {
    const currentMemberIDs = await getMemberIdsForGivenTeamId(currentTeamId);
    newMemberIds = currentMemberIDs ? [...currentMemberIDs, uid] : [uid];
  }

  if (currentTeamId) {
    if (hostId && newMemberIds.length === 1 && newMemberIds[0] === uid) {
      // New team creation: one write for hostId + memberIds
      update(ref(db, '/teams/' + currentTeamId), {
        hostId,
        createdAt: new Date().toISOString(),
        memberIds: { ...newMemberIds },
      });
    } else {
      update(ref(db, '/teams/' + currentTeamId + '/memberIds'), {
        ...newMemberIds,
      });
    }
  }
}

export const leaveTeam = (memberIds, currentTeamId, uid) => {
  //create new memberIds array
  const newMemberIds = memberIds.filter((memberId) => memberId !== uid);
  // set new memberId. Austin, would it be better if we just delete the one spot, rather than updating the whole array? I'd be worried about the edge case of someone joining a team as someone is leaving a team at the same time
  const db = getDatabase();
  set(ref(db, '/teams/' + currentTeamId + '/memberIds'), {
    ...newMemberIds
  });
};

/**
 * When user goes home from lobby: if they're the only member, delete the team;
 * otherwise remove them from the team. Also clears the user's currentTeamId in RTDB.
 */
export async function leaveTeamOrDeleteIfSolo(uid, currentTeamId) {
  if (!uid || !currentTeamId) return;
  const db = getDatabase();
  const memberIdsRaw = await getMemberIdsForGivenTeamId(currentTeamId);
  const memberIds = Array.isArray(memberIdsRaw)
    ? memberIdsRaw
    : (memberIdsRaw ? Object.values(memberIdsRaw) : []);

  const isOnlyMember = memberIds.length === 1 && memberIds[0] === uid;

  if (isOnlyMember) {
    await remove(ref(db, '/teams/' + currentTeamId));
  } else {
    leaveTeam(memberIds, currentTeamId, uid);
  }

  update(ref(db, '/users/' + uid), { currentTeamId: null });
}

/**
 * Clears the user's currentTeamId in RTDB (e.g. when going home from results).
 */
export function clearUserCurrentTeamId(uid) {
  if (!uid) return;
  const db = getDatabase();
  update(ref(db, '/users/' + uid), { currentTeamId: null });
}